import React, { useCallback, useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Label,
} from "recharts";
import {
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  IconButton,
  Box,
  Grid2,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  GetBinChartData,
  GetDLCChartData,
} from "../../../ApiServices/ApiRequests";
import BinCard from "./DLCCard";
import { OverviewChart, WasteMngChartData } from "../../../Models/Models";
import AppColors from "../../../Utils/AppColor";
import {
  useWasteManagement,
  WasteManagementType,
} from "../../../ContextAPI/WasteManagementContext";
import { useTranslation } from "react-i18next";

import DLCCard from "./DLCCard";
import Loading from "../../../Controller/LoadingController/Loading";
import { AppConstants } from "../../../Utils/AppConstants";
import { useMasterData } from "../../../ContextAPI/MasterContext";

interface DLCGraphData {
  month: string;
  [key: string]: number | string;
  Volume: number;
}
const DLCGraph: React.FC = () => {
  const itemsPerPage = 6;
  const { masterState } = useMasterData();
  const { wasteManagementState, dispatchWasteManagement } =
    useWasteManagement();
  const { t } = useTranslation();

  const handleInputChange = useCallback(
    (
      field: keyof WasteManagementType,
      value:
        | string
        | boolean
        | number
        | null
        | OverviewChart
        | WasteMngChartData[]
    ) => {
      dispatchWasteManagement({ type: "SET_FIELD", field, value });
    },
    [dispatchWasteManagement]
  );

  const handleBulkChange = useCallback(
    (value: Partial<WasteManagementType>) => {
      dispatchWasteManagement({ type: "BULK_UPDATE", value });
    },
    [dispatchWasteManagement]
  );
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    const fetchData = async (siteCode: string): Promise<void> => {
      try {
        setLoading(true);
        const result: OverviewChart[] = await GetDLCChartData(
          siteCode,
          wasteManagementState.DLCchartView === "MOM" ? "monthly" : "yearly"
        );

        const chartData: DLCGraphData[] = result.map((item: any) => {
          if (!item.yaxes) {
            return { month: item.xaxes, Volume: 0 };
          }

          const yaxesData: { [key: string]: number } = {};

          item.yaxes.forEach((y: any) => {
            if (y.name && y.value !== undefined) {
              yaxesData[y.name] = parseFloat(y.value) || 0;
            } else {
            }
          });

          const Volume: number = parseFloat(item.zaxes) || 0;

          return {
            month: item.xaxes,
            Volume,
            ...yaxesData,
          };
        });

        console.log("ygjdjdyhjdhjf", chartData);
        handleInputChange("avgDLCChartData", chartData);
      } catch (error) {
        console.error("Error While fetching DICGraph Data");
      } finally {
        setLoading(false);
      }
    };

    if (masterState.selectedSiteCode.length > 0) {
      fetchData(masterState.selectedSiteCode);
    }
  }, [wasteManagementState.DLCchartView, masterState.selectedSiteCode]);

  const handleViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: "MOM" | "YOY"
  ): void => {
    if (newView !== null) {
      handleBulkChange({
        DLCchartView: newView,
        DLCChartPage: 0,
      });
    }
  };

  console.log("wasteManagementState", wasteManagementState.avgDLCChartData);

  const start: number = wasteManagementState.DLCChartPage * itemsPerPage;
  const paginatedData: WasteMngChartData[] =
    wasteManagementState.avgDLCChartData.slice(start, start + itemsPerPage);

  const maxValue: number =
    Math.ceil(
      Math.max(
        ...wasteManagementState.avgDLCChartData.map((item) =>
          Number(item.value)
        )
      ) / 10
    ) * 10;

  const formatYAxisTick = (value: number) => `${value}%`;

  interface RenderLabelProps {
    x: number;
    y: number;
    value: number;
  }

  const renderLabel = ({ x, y, value }: RenderLabelProps) => {
    return (
      <text x={x + 20} y={y + 10} fill="#fff" fontSize={12} fontWeight="bold">
        {value}%
      </text>
    );
  };

  const wasteKeys: string[] =
    wasteManagementState.avgDLCChartData.length > 0
      ? wasteManagementState.avgDLCChartData
          .flatMap((dataItem) => Object.keys(dataItem)) // Get all keys from each object
          .filter((key) => key !== "month" && key !== "Volume") // Filter out "month" and "Volume"
          .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
      : [];
  const colors: string[] = [
    AppConstants.RED,
    AppConstants.SKYBLUE,
    AppConstants.GREEN,
  ];
  // if (loading) {
  //   return <Loading loading={loading} type={`dots`} backDrop={false} />;
  // }
  return (
    <Grid2 container spacing={1}>
      <Grid2 size={{ xs: 8 }}>
        {loading ? (
          <Loading loading={loading} type={`bars`} backDrop={false} />
        ) : (
          <Card
            variant="elevation"
            style={{ width: "100%", margin: "20px auto" }}
          >
            <CardHeader
              title={
                <Typography variant="h6" color={AppColors.textGray}>
                  {t("global.Trends")}
                </Typography>
              }
              subheader={
                <Typography variant="h6">{t("global.DICFillLevel")}</Typography>
              }
              action={
                <ToggleButtonGroup
                  value={wasteManagementState.DLCchartView}
                  exclusive
                  onChange={handleViewChange}
                  aria-label="View Toggle"
                  color="primary"
                >
                  <ToggleButton value="YOY" aria-label="Year-over-Year">
                    {t("global.YOY")}
                  </ToggleButton>
                  <ToggleButton value="MOM" aria-label="Month-over-Month">
                    {t("global.MOM")}
                  </ToggleButton>
                </ToggleButtonGroup>
              }
            />
            <Divider />
            <Box sx={{ mr: 2, mt: 2 }} width={"100%"}>
              {paginatedData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={paginatedData}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis
                      ticks={Array.from(
                        { length: maxValue / 10 + 1 },
                        (_, i) => i * 10
                      )}
                      tickFormatter={formatYAxisTick}
                    >
                      <Label
                        value={t("global.Levelin%")}
                        angle={-90}
                        position="insideLeft"
                      />
                    </YAxis>
                    <Tooltip
                      formatter={(value, name) => [`${value} %`, name]} // Format values with percentage and show field name
                      content={({ payload }) => {
                        if (payload && payload.length) {
                          return (
                            <div
                              style={{
                                backgroundColor: "#fff",
                                border: "1px solid #ccc",
                                padding: "10px",
                              }}
                            >
                              {/* Tooltip heading */}
                              <Typography
                                variant="body1" // Use a suitable variant for the heading
                                style={{
                                  margin: 0,
                                  color: "#000000",
                                }}
                              >
                                {t("global.DIC")}
                              </Typography>

                              {/* Loop through payload to display the data */}
                              {payload.map((entry, index) => (
                                <Typography
                                  key={`item-${index}`}
                                  style={{
                                    margin: 0,
                                    color: entry.color,
                                  }}
                                  variant="body2" // Use a suitable variant for the body text
                                >
                                  {entry.name}: {entry.value}%
                                </Typography>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line type="monotone" dataKey="Volume" stroke="#ff7300" />
                    {wasteKeys.map((key, index) => (
                      <Bar
                        key={index}
                        dataKey={key}
                        fill={colors[index % colors.length]}
                        label={renderLabel}
                        barSize={8}
                        radius={10}
                      />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <Typography
                  variant="h6"
                  color="textSecondary"
                  align="center"
                  style={{ padding: "20px" }}
                >
                  {t("global.NoDataFound")}
                </Typography>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "10px",
                }}
              >
                <IconButton
                  onClick={() => {
                    const newValue = Math.max(
                      wasteManagementState.DLCChartPage - 1,
                      0
                    );
                    handleInputChange("DLCChartPage", newValue);
                  }}
                  disabled={wasteManagementState.DLCChartPage === 0}
                >
                  <ArrowBackIosIcon />
                </IconButton>
                <IconButton
                  onClick={() => {
                    const currentPage = wasteManagementState.DLCChartPage;
                    const newValue = Math.min(
                      currentPage + 1,
                      Math.ceil(
                        wasteManagementState.avgFillStsChartData.length /
                          itemsPerPage
                      ) - 1
                    );
                    handleInputChange("DLCChartPage", newValue);
                  }}
                  disabled={
                    start + itemsPerPage >=
                    wasteManagementState.avgFillStsChartData.length
                  }
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </div>
            </Box>
          </Card>
        )}
      </Grid2>
      <Grid2 size={{ xs: 4 }}>
        <DLCCard />
      </Grid2>
    </Grid2>
  );
};

export default DLCGraph;
